<template>
  <div class="px-4 sm:px-6 lg:px-8 pb-4 h-screen">
    <!-- Search and Filter Bar -->
    <div class="sm:flex sm:items-center mb-6 pt-10">
      <div class="sm:flex-auto">
        <h1 class="text-2xl font-semibold text-gray-900">
          Transaction History
        </h1>
      </div>
      <div class="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search transactions..."
          class="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
        />
      </div>
    </div>

    <!-- Table -->
    <div class="mt-8 flow-root">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div
            class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg"
          >
            <table class="min-w-full divide-y divide-gray-300">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    class="py-3.5 pl-3 pr-3 text-left text-sm font-semibold text-gray-900"
                  >
                    ID
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Bin Code
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Employee ID
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Date
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    Items
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <template
                  v-for="transaction in filteredTransactions"
                  :key="transaction.id"
                >
                  <tr
                    class="hover:bg-gray-50 cursor-pointer"
                    @click="toggleTransaction(transaction.id)"
                  >
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-medium"
                    >
                      {{ transaction.id }}
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >
                      {{ transaction.bin_code || "N/A" }}
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >
                      {{ transaction.employee_id || "N/A" }}
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >
                      {{ formatDate(transaction.registering_date) }}
                    </td>
                    <td class="px-3 py-4 text-sm text-gray-500">
                      <div class="flex items-center">
                        <span class="mr-2"
                          >{{ transaction.items?.length || 0 }} items</span
                        >
                        <span
                          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          :class="
                            expandedTransactions[transaction.id]
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          "
                        >
                          {{
                            expandedTransactions[transaction.id]
                              ? "Hide"
                              : "Show"
                          }}
                          details
                        </span>
                      </div>
                    </td>
                  </tr>

                  <!-- Expanded details row -->
                  <tr
                    v-if="expandedTransactions[transaction.id]"
                    class="bg-gray-50"
                  >
                    <td colspan="5" class="px-3 py-4">
                      <div
                        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                      >
                        <div
                          v-for="(item, index) in transaction.items"
                          :key="index"
                          class="bg-white p-3 rounded-lg shadow-sm border border-gray-200"
                        >
                          <div class="grid grid-cols-2 gap-2 text-sm">
                            <div class="font-medium text-gray-500">
                              Item #{{ index + 1 }}
                            </div>
                            <div class="text-right">
                              <span
                                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                Qty: {{ item.quantity || "0" }}
                              </span>
                            </div>

                            <div class="text-gray-500">Item No</div>
                            <div class="font-medium">
                              {{ item.item_no || "N/A" }}
                            </div>

                            <div class="text-gray-500">Lot No</div>
                            <div class="font-medium">
                              {{ item.lot_no || "N/A" }}
                            </div>

                            <div class="text-gray-500">Patient</div>
                            <div class="font-medium">
                              {{ item.patient_no || "N/A" }}
                            </div>

                            <div class="text-gray-500">Expiry</div>
                            <div class="font-medium">
                              {{
                                item.expiration_date &&
                                item.expiration_date !== "" &&
                                item.expiration_date !== "0001-01-01"
                                  ? formatDate(item.expiration_date)
                                  : "N/A"
                              }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { notify } from "@kyvg/vue3-notification";

export default {
  data() {
    return {
      transactions: [],
      searchQuery: "",
      loading: false,
      error: null,
      expandedTransactions: {}, // Track which transactions are expanded
    };
  },
  computed: {
    filteredTransactions() {
      if (!this.searchQuery) return this.transactions;

      const query = this.searchQuery.toLowerCase();
      return this.transactions.filter((transaction) => {
        return (
          String(transaction.id).toLowerCase().includes(query) ||
          (transaction.bin_code &&
            transaction.bin_code.toLowerCase().includes(query)) ||
          (transaction.employee_id &&
            String(transaction.employee_id).toLowerCase().includes(query)) ||
          (transaction.registering_date &&
            transaction.registering_date.toLowerCase().includes(query)) ||
          (transaction.items &&
            transaction.items.some(
              (item) =>
                (item.item_no && item.item_no.toLowerCase().includes(query)) ||
                (item.lot_no && item.lot_no.toLowerCase().includes(query)) ||
                (item.patient_no &&
                  String(item.patient_no).toLowerCase().includes(query))
            ))
        );
      });
    },
  },
  methods: {
    toggleTransaction(transactionId) {
      this.expandedTransactions = {
        ...this.expandedTransactions,
        [transactionId]: !this.expandedTransactions[transactionId],
      };
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
    async fetchTransactions() {
      this.loading = true;
      this.error = null;

      const token = localStorage.getItem("token");
      if (!token) {
        this.$router.push("/login");
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_SERVICE_URL}get_transections/`,
          {
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
            timeout: 10000,
          }
        );

        console.log("API Response:", response);

        if (response?.data?.data?.transections) {
          console.log("Transactions data:", response.data.data.transections);
          this.transactions = response.data.data.transections;
        } else {
          console.warn("Unexpected API response format:", response.data);
          throw new Error("Invalid response format");
        }
      } catch (error) {
        console.error("Error fetching transactions:", error);
        let errorMessage = "Error loading transactions";

        if (error.response) {
          if (error.response.status === 401) {
            this.$router.push("/login");
            return;
          }
          errorMessage =
            error.response.data?.detail ||
            error.response.data?.message ||
            errorMessage;
        } else if (error.request) {
          errorMessage =
            "No response from server. Please check your connection.";
        } else if (error.message) {
          errorMessage = error.message;
        }

        notify({
          title: errorMessage,
          type: "error",
          duration: 5000,
        });
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.fetchTransactions();
  },
};
</script>
